/* eslint-disable no-await-in-loop */
import i18next from 'i18next';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { isEmpty } from 'utils';
import { getCardPaymentsForIntegration } from 'reducers/Payments';
import { openTerminalIntegration } from 'actions/integrations/terminal';
import {
  markForProcessing,
  deletePayment,
  unmarkFromProcessing,
} from 'actions/Payments';
import { getCafaEntry } from 'reducers/cafaConfigs';
import { INTEGRATION_TYPES, INTEGRATIONS } from 'constants/CAFA';
import { CardPaymentHooks, FUNC_BUTTONS } from 'paymentIntegrations/types';
import { RootState } from 'reducers';

import processPayments from './processPayments';

export const title = 'Tripos Terminal Interface';

export const initPayments = (params: CardPaymentHooks) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const {
    displayError,
    rejectPayments,
    enableButtons,
    resolvePayments,
  } = params;
  const state = getState();
  const { value: config } =
    getCafaEntry<
      typeof INTEGRATION_TYPES.payment,
      typeof INTEGRATIONS.payment.tripos,
      { LaneId: number }
    >(
      INTEGRATIONS[INTEGRATION_TYPES.payment].tripos,
      INTEGRATION_TYPES.payment,
    )(state) || {};

  if (!config || isEmpty(config)) {
    displayError('Tripos not configured.');
    rejectPayments();
    return;
  }
  const cardPayments = getCardPaymentsForIntegration('triPOS')(getState());
  enableButtons([FUNC_BUTTONS.CANCEL]);

  dispatch(processPayments({ ...params, cardPayments })).then(
    ({ errors, data }) => {
      const shouldVoid = getCardPaymentsForIntegration('triPOS')(
        getState(),
      ).every(p => p.shouldProcess && p.paid);

      if (errors.length || !data.length) {
        enableButtons([FUNC_BUTTONS.RETRY, FUNC_BUTTONS.CANCEL]);
      } else if (shouldVoid) {
        rejectPayments();
      } else {
        resolvePayments();
      }
    },
  );
};

export const voidPayments = () => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const cardPayments = getCardPaymentsForIntegration('triPOS')(getState());
  cardPayments.forEach(({ key, paid }) =>
    paid
      ? dispatch(markForProcessing({ key }))
      : dispatch(deletePayment({ key })),
  );

  const finalPayments = getCardPaymentsForIntegration('triPOS')(getState());
  if (finalPayments.length) {
    await dispatch(openTerminalIntegration());
  }
};

export const retryPayment = (params: CardPaymentHooks) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const cardPayments = getCardPaymentsForIntegration('triPOS')(getState());
  if (cardPayments.every(c => c.paid)) {
    Promise.all(
      cardPayments.map(c => dispatch(markForProcessing({ key: c.key }))),
    );
  }
  const cardPaymentsToRetry = getCardPaymentsForIntegration('triPOS')(
    getState(),
  );
  params.enableButtons([]);
  await dispatch(
    initPayments({ ...params, cardPayments: cardPaymentsToRetry }),
  );
};

export const cancelPayments = ({
  rejectPayments,
  enabledButtons,
  updateMessage,
}: CardPaymentHooks) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const integrationCardPayments = getCardPaymentsForIntegration('triPOS')(
    getState(),
  );
  const hasRetry = enabledButtons.includes(FUNC_BUTTONS.RETRY);
  await Promise.all(
    integrationCardPayments.map(p =>
      dispatch(unmarkFromProcessing({ key: p.key })),
    ),
  );
  // Doesn't seem like this integration has a way to cancel a payment from POS, so need to have the cashier cancel it
  if (!hasRetry) {
    updateMessage(i18next.t('paymentIntegrations:extras.cancelFromTerminal'));
  } else {
    rejectPayments();
  }
};

export const returnBack = (params: CardPaymentHooks) => async () =>
  params.rejectPayments();

export const functions = [
  {
    actionOnClick: retryPayment,
    text: 'Retry',
    name: FUNC_BUTTONS.RETRY,
    variant: 'success',
  },
  {
    actionOnClick: cancelPayments,
    text: 'Cancel',
    name: FUNC_BUTTONS.CANCEL,
    variant: 'danger',
  },
];
